import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createStudent } from "../pages/services/studentService";
import PerformanceTable from "./PerformanceTable";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";
import "../styles/AddForm.css";
// import "../styles/AddForm.css";
import "../styles/AddStudent.css";

const DAY_ABBR = { Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "Th", Friday: "F", Saturday: "Sa", Sunday: "Su" };
const formatBatchLabel = (b) => {
  const days = (b.batchDays || []).map(d => DAY_ABBR[d] || d).join("");
  return `${b.batchTitle} | ${days} - ${b.batchTime}`;
};

const HEARD_FROM = ["Facebook", "Instagram", "Linkedin", "College webinar", "Website", "Friend or relative", "Other"];
const STATUSES   = ["Enquiry", "Registered", "Enrolled", "Completed"];
const EDU_LEVELS = ["Schooling", "Graduation", "Post Graduation", "Diploma"];

const CURRENT_YEAR = new Date().getFullYear();
const EDU_YEARS    = Array.from({ length: CURRENT_YEAR - 2009 }, (_, i) => CURRENT_YEAR - i);
const FUTURE_YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR + i);

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const AddStudent = () => {
  const EDU_DEFAULTS = {
    schoolingDetails:      { class: "", schoolName: "", city: "", currentlyPursuing: false, completedIn: "", age: "" },
    graduationDetails:     { courseName: "", currentlyPursuing: false, completedIn: "", branch: "" },
    postGraduationDetails: { courseName: "", currentlyPursuing: false, completedIn: "", branch: "" },
    diplomaDetails:        { courseName: "", currentlyPursuing: false, completedIn: "", branch: "" },
  };

  const [formData, setFormData] = useState({
    studentName: "", contactNumber: "", alternateContactNumber: "",
    DOB: "",
    permanentAddress: "", permanentAddressCity: "", permanentAddressState: "",
    alternateAddress: "", alternateAddressCity: "", alternateAddressState: "",
    emailId: [],
    fatherName: "", fatherOccupation: "", fatherContactNumber: "",
    motherName: "", motherOccupation: "", motherContactNumber: "",
    educationLevel: "",
    schoolCollege: "", schoolCollegePlace: "",
    score: "", branch: "",
    completionYear: "", currentlyPursuing: false, completedIn: "",
    enrollingForCourse: "", heardFrom: "", skillsKnownAlready: "",
    enrolledForCourse: "", totalFee: "", feePaid: "", batchIds: [], status: "", isActive: true,
  });
  const [emailInput, setEmailInput] = useState("");

  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPerformancesDialog, setShowPerformancesDialog] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    // Shared source: never offers an inactive batch, and is not capped by the
    // list endpoint's page size the way a direct /api/batches call was.
    fetchBatchOptions()
      .then(opts => setBatches(opts.map(o => ({ _id: o.value, batchTitle: o.label }))))
      .catch(console.error);
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: "" }));
  };

  const batchOptions = batches.map(b => ({ value: b._id, label: formatBatchLabel(b) }));

  // Mapped from batchIds rather than filtered out of batchOptions, so the chips
  // keep the order they were picked in — batchIds[0] is the primary batch.
  const selectedBatchOptions = formData.batchIds
    .map(id => batchOptions.find(o => String(o.value) === String(id)))
    .filter(Boolean);

  // MultiSelectDropdown deals in {label,value} objects; the form only stores ids.
  const handleBatchChange = (selected) => {
    setFormData(p => ({ ...p, batchIds: selected.map(o => o.value) }));
    setErrors(p => ({ ...p, batchIds: "" }));
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (formData.emailId.includes(email)) {
      setErrors(p => ({ ...p, emailId: "Email already added" }));
      return;
    }
    setFormData(p => ({ ...p, emailId: [...p.emailId, email] }));
    setErrors(p => ({ ...p, emailId: "" }));
    setEmailInput("");
  };

  const removeEmail = (index) => {
    setFormData(p => ({ ...p, emailId: p.emailId.filter((_, i) => i !== index) }));
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addEmail(); }
  };

  const validate = () => {
    const required = [
      "studentName","contactNumber","alternateContactNumber",
      "permanentAddress","permanentAddressCity","permanentAddressState",
      ...(!sameAsPermanent ? ["alternateAddress","alternateAddressCity","alternateAddressState"] : []),
      "emailId","fatherName","fatherOccupation","fatherContactNumber",
      "motherName","motherOccupation","motherContactNumber",
      "educationLevel",
      "enrollingForCourse","heardFrom","batchIds",
    ];
    const labels = {
      studentName:"Student Name", contactNumber:"Contact Number",
      alternateContactNumber:"Alternate Contact", permanentAddress:"Permanent Address",
      permanentAddressCity:"City", permanentAddressState:"State",
      alternateAddress:"Alternate Address", alternateAddressCity:"Alt. City",
      alternateAddressState:"Alt. State", emailId:"Email",
      fatherName:"Father's Name", fatherOccupation:"Father's Occupation",
      fatherContactNumber:"Father's Contact", motherName:"Mother's Name",
      motherOccupation:"Mother's Occupation", motherContactNumber:"Mother's Contact",
      educationLevel:"Last/Current Education",
      enrollingForCourse:"Enrolling For", heardFrom:"Heard From", batchIds:"Batch",
    };
    const errs = {};
    required.forEach(f => {
      if (f === "emailId") {
        if (!formData.emailId || formData.emailId.length === 0) errs.emailId = "At least one email is required";
      } else if (f === "batchIds") {
        if (!formData.batchIds || formData.batchIds.length === 0) errs.batchIds = "At least one batch is required";
      } else if (!String(formData[f] ?? "").trim()) {
        errs[f] = `${labels[f] || f} is required`;
      }
    });

    if (formData.educationLevel && !formData.schoolCollege?.trim()) {
      errs.schoolCollege = "School / College name is required";
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    try {
      const student = await createStudent({ ...formData });
      const studentId = student?._id;
      if (!studentId) throw new Error("Student ID not received.");

      const tempPerformance = JSON.parse(localStorage.getItem("tempPerformances") || "[]");
      if (tempPerformance.length > 0) {
        const performances = tempPerformance.map(p => ({
          ...p, studentId,
          createdDateTime: new Date().toISOString(),
          updatedDateTime: new Date().toISOString(),
        }));
        const res = await fetch(`${API_URL}/api/multiplestudent-performance`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ performances }),
        });
        if (!res.ok) throw new Error("Failed to save performance data");
        localStorage.removeItem("tempPerformances");
      }
      navigate("/students", { state: { successMessage: "Student created successfully!" } });
    } catch (err) {
      // The server names the fields it rejected — mark them, so the message
      // above the form and the fields below it agree. Without this the admin
      // saw a failure with nothing highlighted and no way to tell what to fix.
      if (err.fields && typeof err.fields === "object") {
        // batchId is derived from batchIds server-side; the form only has the
        // latter, so point the error at the input that actually exists.
        const mapped = {};
        Object.entries(err.fields).forEach(([field, message]) => {
          mapped[field === "batchId" ? "batchIds" : field] = message;
        });
        setErrors(p => ({ ...p, ...mapped }));
      }
      setErrorMessage(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inp = (name, placeholder, type = "text") => (
    <input
      type={type} name={name} id={name}
      placeholder={placeholder} value={formData[name]}
      onChange={handleChange}
      className={errors[name] ? "error-input" : ""}
    />
  );

  const sel = (name, options) => (
    <select name={name} value={formData[name]} onChange={handleChange}
      className={errors[name] ? "error-input" : ""}>
      <option value="">Select…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const textarea = (name, placeholder) => (
    <textarea name={name} placeholder={placeholder} value={formData[name]}
      onChange={handleChange} className={errors[name] ? "error-input" : ""} rows={3} />
  );

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving student…</p>
        </div>
      )}

      <div className="af-card">
        {/* Header */}
        <div className="af-header">
          <div className="af-header-icon indigo">🎓</div>
          <div className="af-header-text">
            <h2 className="af-title">Add New Student</h2>
            <p className="af-subtitle">Fill in all required fields to register the student</p>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && <div className="af-alert af-alert-success" style={{margin:"16px 32px 0"}}>{successMessage}</div>}
        {errorMessage   && <div className="af-alert af-alert-error"   style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* Personal */}
            <div className="af-section">
              <p className="af-section-title">Personal Information</p>
              <Field label="Full Name" required error={errors.studentName}>
                {inp("studentName", "e.g. Rahul Sharma")}
              </Field>
              <div className="af-row">
                <Field label="Email Address(es)" required error={errors.emailId}>
                  <div className={`af-email-tags-wrapper${errors.emailId ? " error-input" : ""}`}>
                    {formData.emailId.map((email, i) => (
                      <span key={i} className="af-email-tag">
                        {email}
                        <button type="button" className="af-email-tag-remove" onClick={() => removeEmail(i)}>×</button>
                      </span>
                    ))}
                    <input
                      type="email"
                      className="af-email-tag-input"
                      placeholder="Add email and press Enter"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={handleEmailKeyDown}
                    />
                  </div>
                  <button type="button" className="af-email-add-btn" onClick={addEmail}>+ Add</button>
                </Field>
                <Field label="Status" error={errors.status}>
                  {sel("status", STATUSES)}
                </Field>
                <Field label="Visibility" error={errors.isActive}>
                  <label className="af-switch">
                    <input
                      type="checkbox"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                    />
                    <span>{formData.isActive !== false ? "Active" : "Inactive"}</span>
                  </label>
                  <span className="af-hint">
                    Inactive students are hidden from lists and filter dropdowns across
                    the Admin and Trainer apps. Nothing is deleted, and the student keeps access to their own app.
                  </span>
                </Field>
              </div>
              <div className="af-row">
                <Field label="Contact Number" required error={errors.contactNumber}>
                  {inp("contactNumber", "10-digit mobile")}
                </Field>
                <Field label="Alternate Contact" required error={errors.alternateContactNumber}>
                  {inp("alternateContactNumber", "Alternate number")}
                </Field>
              </div>
              <div className="af-row">
                <Field label="Date of Birth" error={errors.DOB}>
                  {inp("DOB", "", "date")}
                </Field>
                <Field label="Heard From" required error={errors.heardFrom}>
                  {sel("heardFrom", HEARD_FROM)}
                </Field>
              </div>
            </div>

            {/* Permanent Address */}
            <div className="af-section">
              <p className="af-section-title">Permanent Address</p>
              <Field label="Address" required error={errors.permanentAddress}>
                {textarea("permanentAddress", "House/Flat, Street, Area")}
              </Field>
              <div className="af-row">
                <Field label="City" required error={errors.permanentAddressCity}>
                  {inp("permanentAddressCity", "City")}
                </Field>
                <Field label="State" required error={errors.permanentAddressState}>
                  {inp("permanentAddressState", "State")}
                </Field>
              </div>
            </div>

            {/* Alternate Address */}
            <div className="af-section">
              <div className="af-section-header-row">
                <p className="af-section-title">Alternate Address</p>
                <label className="af-same-address-label">
                  <input
                    type="checkbox"
                    className="af-same-address-checkbox"
                    checked={sameAsPermanent}
                    onChange={(e) => {
                      setSameAsPermanent(e.target.checked);
                      if (e.target.checked) {
                        setErrors(p => ({ ...p, alternateAddress: "", alternateAddressCity: "", alternateAddressState: "" }));
                      }
                    }}
                  />
                  Permanent address is the current address
                </label>
              </div>

              {!sameAsPermanent && (
                <>
                  <Field label="Address" required error={errors.alternateAddress}>
                    {textarea("alternateAddress", "House/Flat, Street, Area")}
                  </Field>
                  <div className="af-row">
                    <Field label="City" required error={errors.alternateAddressCity}>
                      {inp("alternateAddressCity", "City")}
                    </Field>
                    <Field label="State" required error={errors.alternateAddressState}>
                      {inp("alternateAddressState", "State")}
                    </Field>
                  </div>
                </>
              )}
            </div>

            {/* Family */}
            <div className="af-section">
              <p className="af-section-title">Family Details</p>
              <div className="af-row">
                <Field label="Father's Name" required error={errors.fatherName}>
                  {inp("fatherName", "Full name")}
                </Field>
                <Field label="Father's Occupation" required error={errors.fatherOccupation}>
                  {inp("fatherOccupation", "Occupation")}
                </Field>
              </div>
              <Field label="Father's Contact" required error={errors.fatherContactNumber}>
                {inp("fatherContactNumber", "Contact number")}
              </Field>
              <div className="af-row">
                <Field label="Mother's Name" required error={errors.motherName}>
                  {inp("motherName", "Full name")}
                </Field>
                <Field label="Mother's Occupation" required error={errors.motherOccupation}>
                  {inp("motherOccupation", "Occupation")}
                </Field>
              </div>
              <Field label="Mother's Contact" required error={errors.motherContactNumber}>
                {inp("motherContactNumber", "Contact number")}
              </Field>
            </div>

            {/* Education */}
            <div className="af-section">
              <p className="af-section-title">Education</p>

              <Field label="Last / Current Education" required error={errors.educationLevel}>
                {sel("educationLevel", EDU_LEVELS)}
              </Field>

              {formData.educationLevel && (
                <>
                  <div className="af-row">
                    <Field
                      label={formData.educationLevel === "Schooling" ? "School Name" : "College / Institution"}
                      required
                      error={errors.schoolCollege}
                    >
                      <input
                        type="text" name="schoolCollege"
                        placeholder={formData.educationLevel === "Schooling" ? "School name" : "College / Institution name"}
                        value={formData.schoolCollege}
                        onChange={handleChange}
                        className={errors.schoolCollege ? "error-input" : ""}
                      />
                    </Field>
                    <Field label="City" error={errors.schoolCollegePlace}>
                      <input
                        type="text" name="schoolCollegePlace"
                        placeholder="City"
                        value={formData.schoolCollegePlace}
                        onChange={handleChange}
                      />
                    </Field>
                  </div>

                  <div className="af-row">
                    <Field
                      label={formData.educationLevel === "Schooling" ? "Class / Grade" : "Branch / Specialization"}
                      error={errors.branch}
                    >
                      <input
                        type="text" name="branch"
                        placeholder={formData.educationLevel === "Schooling" ? "e.g. 10th Grade" : "e.g. Computer Science"}
                        value={formData.branch}
                        onChange={handleChange}
                      />
                    </Field>
                    <Field label="Score / CGPA / Marks" error={errors.score}>
                      <input
                        type="text" name="score"
                        placeholder="e.g. 8.5 or 75%"
                        value={formData.score}
                        onChange={handleChange}
                      />
                    </Field>
                  </div>

                  <label className="af-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.currentlyPursuing}
                      onChange={e => {
                        setFormData(p => ({
                          ...p,
                          currentlyPursuing: e.target.checked,
                          completedIn: e.target.checked ? "" : p.completedIn,
                          completionYear: !e.target.checked ? "" : p.completionYear,
                        }));
                      }}
                    />
                    Currently Pursuing
                  </label>

                  {formData.currentlyPursuing ? (
                    <Field label="Expected Completion Year" error={errors.completionYear}>
                      <select name="completionYear" value={formData.completionYear} onChange={handleChange}>
                        <option value="">Select year…</option>
                        {FUTURE_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Completed In" error={errors.completedIn}>
                      <select name="completedIn" value={formData.completedIn} onChange={handleChange}>
                        <option value="">Select year…</option>
                        {EDU_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </Field>
                  )}
                </>
              )}
            </div>

            {/* Course */}
            <div className="af-section">
              <p className="af-section-title">Course & Skills</p>
              <Field label="Enrolling For Course" required error={errors.enrollingForCourse}>
                <select name="enrollingForCourse" value={formData.enrollingForCourse}
                  onChange={handleChange} className={errors.enrollingForCourse ? "error-input" : ""}>
                  <option value="">Select batch…</option>
                  {batches.map(b => <option key={b._id} value={b.batchTitle}>{formatBatchLabel(b)}</option>)}
                </select>
              </Field>
              <Field label="Enrolled For Course" error={errors.enrolledForCourse}>
                <select name="enrolledForCourse" value={formData.enrolledForCourse}
                  onChange={handleChange}>
                  <option value="">Select batch…</option>
                  {batches.map(b => <option key={b._id} value={b.batchTitle}>{formatBatchLabel(b)}</option>)}
                </select>
              </Field>
              <Field label="Skills Known Already" error={errors.skillsKnownAlready}>
                {inp("skillsKnownAlready", "e.g. HTML, CSS, JavaScript")}
              </Field>
            </div>

            {/* Financials */}
            <div className="af-section">
              <p className="af-section-title">Batch & Financials</p>
              <Field label="Assign Batches" required error={errors.batchIds}>
                <MultiSelectDropdown
                  options={batchOptions}
                  value={selectedBatchOptions}
                  onChange={handleBatchChange}
                  placeholder="Select one or more batches…"
                  showCheckboxes
                />
                <span className="af-hint">
                  The first batch selected becomes the student's primary batch.
                </span>
              </Field>
              <div className="af-row">
                <Field label="Total Fee (₹)" error={errors.totalFee}>
                  {inp("totalFee", "e.g. 25000", "number")}
                </Field>
                <Field label="Fee Paid (₹)" error={errors.feePaid}>
                  {inp("feePaid", "e.g. 10000", "number")}
                </Field>
              </div>
            </div>

            {/* Performance */}
            <div className="af-section">
              <p className="af-section-title">Performance (Optional)</p>
              <div className="af-action-strip">
                <button type="button" className="af-secondary-btn"
                  onClick={() => setShowPerformancesDialog(true)}>
                  + Add Performance Entry
                </button>
              </div>
              {errors.performance && <span className="af-error">{errors.performance}</span>}
              <PerformanceTable
                studentId={formData._id}
                openPerformancesDialogExternally={showPerformancesDialog}
                setOpenPerformancesDialogExternally={setShowPerformancesDialog}
              />
            </div>
          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Saving…" : "Register Student"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStudent;
